import type { GetRowIdParams } from 'ag-grid-community';
import classnames from 'classnames';
import Filter from 'components/Blotters/SecondaryMarketTrade/filter/FilterWithCsvExport';
import { SecondaryMarketBlotterGrid } from 'components/Blotters/SecondaryMarketTrade/grid/Grid';
import { ConnectedPagination } from 'components/Blotters/SecondaryMarketTrade/pagination/Pagination';
import { PreferencesLoadingErrorModal } from 'components/Blotters/SecondaryMarketTrade/preferencesLoadingErrorModal';
import { getStatusFieldColor } from 'components/shared/Blotter/BlotterPanel/Fields/StatusField/StatusField';
import { useClientSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useClientSideDefaultColumnTypes';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { useStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import type { IBlotterItem } from 'models/IBlotterItem';
import { FC, useCallback, useEffect, useMemo } from 'react';

import { useAgGridApiState } from '../../components/shared/Blotter/Grid/Hooks/useApiState';
import { useContextMenuItems } from '../../components/shared/Blotter/Grid/Hooks/useContextMenuItems';
import type { ComponentProps } from './Component.connect';
import { connectSecondaryMarketTradeBlotter } from './Component.connect';
import { SidePanel } from './side-panel';

const getHeaderIntlKey = getHeaderForBlotterIntlKey('secondaryMarketBlotter');

const Component: FC<ComponentProps> = ({ api, onReset, hasSelectedItem, selected, onReloadQuote }) => {
  const [gridApi, setGridApi] = useAgGridApiState<IBlotterItem>();

  const onPanelClose = useCallback(() => {
    onReset();
  }, [onReset]);

  useEffect(() => {
    onReset();
  }, [onReset]);

  const getRowId = useCallback((p: GetRowIdParams) => p.data['quoteId'], []);

  const getContextMenuItems = useContextMenuItems('Blotter Secondary Market trades', gridApi);
  const defaultColumnDefinition = useDefaultColumnDefinition<IBlotterItem>(getHeaderIntlKey);
  const statusColumn = useStatusColumnType<string, IBlotterItem>(getStatusFieldColor);
  const customColumns = useMemo(
    () => ({
      status: statusColumn,
    }),
    [statusColumn],
  );
  const columnTypes = useClientSideDefaultColumnTypes<IBlotterItem>(customColumns);

  return (
    <PreferencesLoadingErrorModal>
      <div
        className="page-container secondary-trade-blotter d-flex flex-column justify-content-between w-100 h-100"
        data-e2e="secondary-market-trade-blotter-container"
      >
        <Filter fetchCsvApi={api} />
        <div className="container-fluid flex-fill bg-lvl1 d-flex p-0 h-100 gap-4">
          <div
            className={classnames(
              'flex-grow-1 flex-shrink-1 d-flex flex-column vh-82',
              hasSelectedItem ? 'col-md-6 col-lg-8 col-xl-9 col-xxl-9' : 'col-12',
            )}
          >
            <SecondaryMarketBlotterGrid
              name="secondaryMarketBlotter"
              logAnalyticName="Blotter Secondary Market trades"
              e2e="secondary-trade-blotter-grid"
              getRowId={getRowId}
              columnTypes={columnTypes}
              defaultColDef={defaultColumnDefinition}
              onGridReady={setGridApi}
              getContextMenuItems={getContextMenuItems}
            />
            <ConnectedPagination />
          </div>

          {hasSelectedItem && (
            <div className="col-md-6 col-lg-4 col-xl-3 col-xxl-3 px-0">
              <SidePanel
                className="border-start h-100"
                onClose={onPanelClose}
                selected={selected!}
                onReloadQuote={onReloadQuote}
              />
            </div>
          )}
        </div>
      </div>
    </PreferencesLoadingErrorModal>
  );
};

Component.displayName = 'SecondaryTradeBlotter';
export default connectSecondaryMarketTradeBlotter(Component);
